import React, { useState, useEffect, useMemo } from 'react';
import Button from '@components/common/Button';
import './JSSKDetails.css';
import '../../Child/ChildComponent.css';
import { Checkbox, message,Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  JSSK_SUBCATEGORY_DISPLAY_NAME_MAP,
  JSSK_NON_EDITABLE_CATEGORY,
  JSSK_FREE_DIAGNOSTICS_CATEGORY,
  JSSK_ALWAYS_DISABLED_DIAGNOSTICS,
} from '../../../../../constants/jsskConstants';
import { getData, postDataJSON } from '@services/CommonApiService';
import endpoints from '@config/endpoints';
import { errorLogger } from '@utils/applicationLogUtils';
interface JSSKDetailsProps {
  pregnancyData: any;
  onBack: () => void;
}

interface JSSKService {
  pregnancy_linked_jssk_service_id: string;
  service_category: string;
  service_category_code: string;
  service_sub_category: string;
  service_id: string;
  service_name: string;
  is_service_taken: boolean;
  is_service_taken_under_jssk: boolean,
  service_category_order: number | null;     
  service_sub_category_order: number | null; 
  service_order: number | null;  
}

interface GroupedServices {
  [category: string]: {
    [subCategory: string]: JSSKService[];
  };
}

const JSSKDetails: React.FC<JSSKDetailsProps> = ({
  pregnancyData,
  onBack,
}) => {
  const pregnancyId = pregnancyData?.pregnancy_id;

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [services, setServices] = useState<JSSKService[]>([]);
  const [selectedServices, setSelectedServices] = useState<{
    [key: string]: boolean;
  }>({});

  const normalizeText = (value?: string): string =>
    value?.trim().toLowerCase() || '';

  const isFreeDrugsAndConsumables = (service: JSSKService): boolean =>
    service.service_category_code === JSSK_NON_EDITABLE_CATEGORY;




  const getJSSKDisplaySubCategory = (
    subCategory: string,
  ): React.ReactNode => {

    return JSSK_SUBCATEGORY_DISPLAY_NAME_MAP[subCategory] || subCategory;
  };

const getServiceSortOrder = (service: JSSKService): number =>
  service.service_order ?? 999;

  const getDiagnosticTooltipMessage = (service: JSSKService): string | null => {
    const isFreeDiagnostics =
      service.service_category_code === JSSK_FREE_DIAGNOSTICS_CATEGORY;

    const serviceName = normalizeText(service.service_name);
    if (JSSK_ALWAYS_DISABLED_DIAGNOSTICS.includes(service.service_name)) {
      return null;
    }
    if (isFreeDiagnostics && !service.is_service_taken) {
      return `Add "${
        service.service_name
      }" to ANC services to enable this diagnostic.`;
    }

    return null;
  };

  const groupedServices = useMemo(() => {
    const grouped: GroupedServices = {};

    services.forEach((service) => {

      const category = service.service_category;
      const subCategory = service.service_sub_category || '';
      if (!grouped[category]) {
        grouped[category] = {};
      }

      if (!grouped[category][subCategory]) {
        grouped[category][subCategory] = [];
      }

      grouped[category][subCategory].push(
        service,
      );
    });

    return grouped;
  }, [services]);

  const fetchJSSKServices = async () => {
    if (!pregnancyId) {
      message.error('Pregnancy ID is missing');
      return;
    }

     if (pregnancyData?.is_pregnancy_outside_rch) {
      return;
    }

    try {
      setLoading(true);

      const response = await getData<any>(
        `${endpoints.getJSSKServiceDetails(pregnancyId)}`,
      );

      const responseData: JSSKService[] = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

      setServices(responseData);

      const initialSelection: { [key: string]: boolean } = {};

      responseData.forEach((service) => {
        initialSelection[service.pregnancy_linked_jssk_service_id] =
          service.is_service_taken_under_jssk;
      });

      setSelectedServices(initialSelection);
    } catch (error: any) {
      errorLogger(error?.response?.data || error?.response || error);
      message.error('Failed to load JSSK services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJSSKServices();
  }, [pregnancyId]);

  const isCheckboxDisabled = (service: JSSKService): boolean => {
    if (service.service_category_code === JSSK_NON_EDITABLE_CATEGORY) {
      return true;
    }

    if (JSSK_ALWAYS_DISABLED_DIAGNOSTICS.includes(
      service.service_name,
    )
  ) {
    return true;
  }

    return !service.is_service_taken;
  };



  const isCheckboxChecked = (service: JSSKService): boolean => {
  return selectedServices[service.pregnancy_linked_jssk_service_id] || false;
};
  const handleCheckboxChange = (
    service: JSSKService,
    checked: boolean,
  ) => {
    setSelectedServices((prev) => ({
      ...prev,
      [service.pregnancy_linked_jssk_service_id]: checked,
    }));
  };

  const handleSave = async () => {
    if (!pregnancyId) {
      message.error('Pregnancy ID is missing');
      return;
    }

    const serviceDetails = services.map((service) => ({
      pregnancy_linked_jssk_service_id:
        service.pregnancy_linked_jssk_service_id,

      is_service_taken_under_jssk:
        selectedServices[service.pregnancy_linked_jssk_service_id] || false,
    }));

    const payload = {
      serviceDetails,
      updatedBy: localStorage.getItem('userId') || '',
    };

    try {
      setSaving(true);

      const response = await postDataJSON<any>(
        endpoints.saveJSSKServiceDetails,
        payload,
      );

      if (response?.data?.message) {
        message.success(response.data.message);
        onBack();
      } else {
        message.error('Failed to save JSSK services');
      }
    } catch (error: any) {
      errorLogger(error?.response?.data || error?.response || error);
      message.error('Failed to save JSSK services');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onBack();
  };

  const renderCheckbox = (
    service: JSSKService,
    disabledClassName = '',
  ) => {

    const tooltipMessage = getDiagnosticTooltipMessage(service);

    return (
      <div
        className={`jssk-checkbox-item ${disabledClassName}`}
        key={service.pregnancy_linked_jssk_service_id}
      >
        <Checkbox
          checked={isCheckboxChecked(service)}
          disabled={isCheckboxDisabled(service)}
          onChange={(e) =>
            handleCheckboxChange(service, e.target.checked)
          }
        >
          {service.service_name}
         </Checkbox>
           {tooltipMessage && (
          <Tooltip title={tooltipMessage}>
            <InfoCircleOutlined
              style={{
                marginLeft: 6,
                cursor: 'pointer',
                color: '#8c8c8c',
              }}
            />
          </Tooltip>
    )}
   </div>
    )
  }

  const getSortedServices = (
    category: string,
    subCategory: string,
  ): JSSKService[] => {
    return [...groupedServices[category][subCategory]].sort(
      (a, b) => getServiceSortOrder(a) - getServiceSortOrder(b),
    );
  };

  const getFlattenedSortedServicesByCategory = (
    category: string,
  ): JSSKService[] => {
    return Object.keys(groupedServices[category])
      .flatMap((subCategory) => groupedServices[category][subCategory])
      .sort((a, b) => getServiceSortOrder(a) - getServiceSortOrder(b));
  };

  return (
    <div className="jssk-pregnancy-list-container">
      <div className="pregnancy-list-content">
        <div className="jssk-details-wrapper">
          <div className="jssk-details-container">
            <div className="jssk-actions">
              <Button
                onClick={handleCancel}
                buttonName="Cancel"
                colorType="secondary"
                id="jssk-cancel-btn"
                disabled={saving}
              />

              <Button
                onClick={handleSave}
                buttonName={saving ? 'Saving...' : 'Save'}
                colorType="primary"
                id="jssk-save-btn"
                disabled={saving || loading}
              />
            </div>

            <div className="jssk-details-content">
              {loading ? (
                <div className="jssk-loading">Loading JSSK Services...</div>
              ) : services.length === 0 ? (
                <div className="jssk-no-data">
                  <p>No JSSK services found for this pregnancy</p>
                </div>
              ) : (
                <>
                  {(() => {
                    const sortedCategories = Object.keys(groupedServices).sort(
                      (a, b) => {
                        const orderA = services.find((s) => s.service_category === a)?.service_category_order ?? 999;
                        const orderB = services.find((s) => s.service_category === b)?.service_category_order ?? 999;
                        return orderA - orderB;
                      },
                    );

                    return sortedCategories.map((category) => {
                      const isConsumables =
                        services.find((s) => s.service_category === category)?.service_category_code === JSSK_NON_EDITABLE_CATEGORY;

                      if (isConsumables) {
                        const subCategories = Object.keys(groupedServices[category]).sort(
                          (a, b) => {
                            const orderA = services.find((s) => s.service_category === category && s.service_sub_category === a)?.service_sub_category_order ?? 999;
                            const orderB = services.find((s) => s.service_category === category && s.service_sub_category === b)?.service_sub_category_order ?? 999;
                            return orderA - orderB;
                          },
                        );

                        return (
                          <div className="jssk-section" key={category}>
                            <div className="jssk-section-header">
                              {category}
                            </div>

                            <div className="jssk-subsections-container">
                              {subCategories.map((subCategory) => (
                                <div
                                  className="jssk-subsection-vertical"
                                  key={subCategory}
                                >
                                  {subCategory && (
                                     <h3 className="jssk-subsection-title">
                                    {getJSSKDisplaySubCategory(subCategory)}
                                  </h3>
                                )}
                                 
                                  <div className="jssk-checkbox-grid">
                                    {getSortedServices(
                                      category,
                                      subCategory,
                                    ).map((service) =>
                                      renderCheckbox(
                                        service,
                                        'jssk-checkbox-disabled',
                                      ),
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="jssk-section" key={category}>
                          <div className="jssk-section-header">{category}</div>

                          {Object.keys(groupedServices[category])
                            .sort((a, b) => {
                              const orderA = services.find((s) => s.service_category === category && s.service_sub_category === a)?.service_sub_category_order ?? 999;
                              const orderB = services.find((s) => s.service_category === category && s.service_sub_category === b)?.service_sub_category_order ?? 999;
                              return orderA - orderB;
                            })
                            .map(
                            (subCategory) => (
                              <div
                                className="jssk-subsection"
                                key={`${category}-${subCategory}`}
                              >
                                {subCategory && (
                                   <h3 className="jssk-subsection-title">
                                  {getJSSKDisplaySubCategory(subCategory)}
                                </h3>
                                )}

                                <div className="jssk-checkbox-grid">
                                  {[...groupedServices[category][subCategory]]
                                    .sort(
                                      (a, b) =>
                                        getServiceSortOrder(a) -
                                        getServiceSortOrder(b),
                                    )
                                    .map((service) => renderCheckbox(service))}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      );
                    });
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JSSKDetails;